/**
 * Formats a date string or Date object to a human-readable format.
 * Format: 'DD MMM YYYY'
 * Example: '01 Jan 2023'
 *
 * @param {string | null} value, a date string or Date object
 * @returns {string} formatted date
 */
export const formatDateAsDayMonthYear = value => {
  if (value === null || value === undefined) {
    return '';
  }
  return new Date(value).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

/**
 * Formats a date relative to the current time for AI Assistant sessions and messages.
 * - If within 24 hours: shows time (e.g., "2:30 PM")
 * - If within 1 week: shows weekday (e.g., "Mon")
 * - If older: shows month and day (e.g., "Jan 15")
 *
 * @param {string|Date} date - The date to format
 * @returns {string} The formatted relative date string
 */
export const formatRelativeDate = date => {
  const now = new Date();
  const targetDate = new Date(date);
  const diffInHours = (now - targetDate) / (1000 * 60 * 60);

  if (diffInHours < 24) {
    return targetDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (diffInHours < 24 * 7) {
    return targetDate.toLocaleDateString([], { weekday: 'short' });
  } else {
    return targetDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
};

/**
 * Creates a date with a year offset from the current date.
 * Returns the date as an ISO date string in YYYY-MM-DD format.
 *
 * @param {number} yearOffset - The number of years to add to the current year (can be negative)
 * @returns {string} ISO date string in YYYY-MM-DD format
 * @example
 * // Get date 10 years from now
 * createDateWithYearOffset(10) // "2033-09-30"
 *
 * // Get date 10 years ago
 * createDateWithYearOffset(-10) // "2014-09-30"
 */
export const createDateWithYearOffset = yearOffset => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + yearOffset);
  return date.toISOString().split('T')[0];
};

/**
 * Formats a date string or Date object to numeric format.
 * Format: 'MM/DD/YYYY'
 * Example: '10/16/2025'
 *
 * @param {string | Date | null} value - a date string or Date object
 * @returns {string} formatted date
 */
export const formatDateAsNumeric = value => {
  if (value === null || value === undefined) {
    return '';
  }
  return new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  });
};
